"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { FC, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MemesArtSubmissionShell } from "./MemesArtSubmissionShell";
import { MemesArtSubmissionStepContent } from "./MemesArtSubmissionStepContent";
import { ResubmitAcknowledgement } from "./ResubmitAcknowledgement";
import { ResubmitDeleteConfirmation } from "./ResubmitDeleteConfirmation";
import { useArtworkSubmissionForm } from "./hooks/useArtworkSubmissionForm";
import { useArtworkSubmissionMutation } from "./hooks/useArtworkSubmissionMutation";
import { useResubmissionDelete } from "./hooks/useResubmissionDelete";
import type { SubmissionPhase } from "./ui/SubmissionProgress";
import { buildPreviewDrop } from "./utils/buildPreviewDrop";
import { buildMemesSubmissionDraftFromDrop } from "./utils/submissionDraft";
interface MemesArtSubmissionContainerProps {
  readonly onClose: () => void;
  readonly wave: ApiWave;
  readonly sourceDrop?: ExtendedDrop | undefined;
  readonly onSourceDropDeleted?: (() => void) | undefined;
}

/**
 * MemesArtSubmissionContainer - Main container component for the artwork submission flow
 *
 * This component has been simplified by:
 * 1. Moving form state management to a custom hook with reducer pattern
 * 2. Extracting the modal layout to a separate component
 * 3. Using an enum with a component map for cleaner step routing
 * 4. Using direct component composition instead of component injection
 * 5. Using a separate mutation hook for API submission
 * 6. Using a dedicated progress component for visual feedback
 */
const MemesArtSubmissionContainer: FC<MemesArtSubmissionContainerProps> = t0 => {
  const $ = _c(70);
  const {
    onClose,
    wave,
    sourceDrop,
    onSourceDropDeleted
  } = t0;
  let t1;
  if ($[0] !== sourceDrop) {
    t1 = sourceDrop ? buildMemesSubmissionDraftFromDrop(sourceDrop) : undefined;
    $[0] = sourceDrop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialDraft = t1;
  const isResubmission = Boolean(sourceDrop);
  const submitLabel = isResubmission ? "Submit New Version" : "Submit Artwork";
  const [hasAcknowledgedResubmission, setHasAcknowledgedResubmission] = useState(!isResubmission);
  const form = useArtworkSubmissionForm(initialDraft);
  const {
    handleBackToArtwork,
    setAdditionalMedia
  } = form;
  const {
    connectedProfile
  } = useAuth();
  const {
    isSafeWallet,
    address
  } = useSeizeConnectContext();
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewDrop, setPreviewDrop] = useState(null);
  const {
    submitArtwork,
    uploadProgress,
    submissionPhase,
    submissionError,
    isSubmitting
  } = useArtworkSubmissionMutation();
  let t2;
  if ($[2] !== onClose || $[3] !== onSourceDropDeleted || $[4] !== sourceDrop) {
    t2 = {
      sourceDrop,
      onClose,
      onSourceDropDeleted
    };
    $[2] = onClose;
    $[3] = onSourceDropDeleted;
    $[4] = sourceDrop;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    replacementDrop,
    isDeletingOriginal,
    deleteOriginalError,
    handleResubmissionSuccess,
    handleDeleteOriginalClick,
    handleKeepBoth
  } = useResubmissionDelete(t2);
  let t3;
  let t4;
  if ($[6] !== isResubmission || $[7] !== onClose || $[8] !== submissionPhase) {
    t3 = () => {
      if (isResubmission) {
        return;
      }
      if (submissionPhase !== "success") {
        return;
      }
      const timer = setTimeout(() => {
        onClose();
      }, 1200);
      return () => clearTimeout(timer);
    };
    t4 = [isResubmission, submissionPhase, onClose];
    $[6] = isResubmission;
    $[7] = onClose;
    $[8] = submissionPhase;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsPreviewMode(false);
      setPreviewDrop(null);
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const resetPreviewState = t5;
  let t6;
  if ($[12] !== setAdditionalMedia) {
    t6 = media => {
      setAdditionalMedia({
        artwork_commentary_media: media
      });
    };
    $[12] = setAdditionalMedia;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleArtworkCommentaryMediaChange = t6;
  let t7;
  if ($[14] !== setAdditionalMedia) {
    t7 = url => {
      setAdditionalMedia({
        preview_image: url
      });
    };
    $[14] = setAdditionalMedia;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handlePreviewImageChange = t7;
  let t8;
  if ($[16] !== setAdditionalMedia) {
    t8 = url_0 => {
      setAdditionalMedia({
        promo_video: url_0
      });
    };
    $[16] = setAdditionalMedia;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handlePromoVideoChange = t8;
  const handlePhaseChange = _temp;
  let t9;
  if ($[18] !== connectedProfile || $[19] !== form || $[20] !== wave) {
    t9 = () => {
      const {
        imageUrl,
        traits,
        operationalData,
        isAdditionalActionPromised
      } = form.getSubmissionData();
      const media_0 = form.getMediaSelection();
      setPreviewDrop(buildPreviewDrop({
        wave,
        traits,
        operationalData,
        isAdditionalActionPromised,
        mediaSelection: media_0,
        uploadArtworkUrl: imageUrl,
        connectedProfile
      }));
      setIsPreviewMode(true);
    };
    $[18] = connectedProfile;
    $[19] = form;
    $[20] = wave;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleOpenPreview = t9;
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setIsPreviewMode(false);
    };
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const handleBackToEdit = t10;
  let t11;
  if ($[23] !== handleBackToArtwork) {
    t11 = () => {
      resetPreviewState();
      handleBackToArtwork();
    };
    $[23] = handleBackToArtwork;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const handleBackFromAdditionalInfo = t11;
  let t12;
  if ($[25] !== handleResubmissionSuccess) {
    t12 = async drop => {
      await handleResubmissionSuccess(drop);
      resetPreviewState();
    };
    $[25] = handleResubmissionSuccess;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const handleResubmissionSubmitted = t12;
  let t13;
  if ($[27] !== address || $[28] !== form || $[29] !== handleResubmissionSubmitted || $[30] !== isResubmission || $[31] !== isSafeWallet || $[32] !== submitArtwork || $[33] !== wave.id || $[34] !== wave.participation.terms) {
    t13 = async () => {
      const {
        traits: traits_0,
        operationalData: operationalData_0,
        isAdditionalActionPromised: isAdditionalActionPromised_0
      } = form.getSubmissionData();
      const media_1 = form.getMediaSelection();
      const onSubmitted = async drop_0 => {
        if (drop_0 && isResubmission) {
          await handleResubmissionSubmitted(drop_0);
        }
        return drop_0;
      };
      if (media_1.mediaSource === "upload") {
        if (!media_1.selectedFile && !media_1.existingMedia) {
          return null;
        }
        const result = await submitArtwork({
          ...(media_1.selectedFile ? {
            imageFile: media_1.selectedFile
          } : {}),
          ...(media_1.existingMedia ? {
            existingMedia: media_1.existingMedia
          } : {}),
          traits: traits_0,
          operationalData: operationalData_0,
          isAdditionalActionPromised: isAdditionalActionPromised_0,
          waveId: wave.id,
          termsOfService: wave.participation.terms
        }, address ?? "", isSafeWallet, {
          onPhaseChange: handlePhaseChange
        });
        return onSubmitted(result);
      }
      if (!media_1.isExternalValid) {
        return null;
      }
      const result_0 = await submitArtwork({
        externalMedia: {
          url: media_1.externalUrl,
          mimeType: media_1.externalMimeType
        },
        traits: traits_0,
        operationalData: operationalData_0,
        isAdditionalActionPromised: isAdditionalActionPromised_0,
        waveId: wave.id,
        termsOfService: wave.participation.terms
      }, address ?? "", isSafeWallet, {
        onPhaseChange: handlePhaseChange
      });
      return onSubmitted(result_0);
    };
    $[27] = address;
    $[28] = form;
    $[29] = handleResubmissionSubmitted;
    $[30] = isResubmission;
    $[31] = isSafeWallet;
    $[32] = submitArtwork;
    $[33] = wave.id;
    $[34] = wave.participation.terms;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const handleSubmit = t13;
  let t14;
  if ($[36] !== handleSubmit) {
    t14 = () => {
      handleSubmit();
    };
    $[36] = handleSubmit;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const handleSubmitClick = t14;
  let t15;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setHasAcknowledgedResubmission(true);
    };
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const handleAcceptResubmissionAcknowledgement = t15;
  const shellDescription = isResubmission && hasAcknowledgedResubmission && !replacementDrop ? "Resubmitting creates a new submission with this data, then asks you to confirm deleting the original." : undefined;
  let submissionContent;
  if (isResubmission && !hasAcknowledgedResubmission) {
    let t16;
    if ($[39] !== onClose) {
      t16 = <ResubmitAcknowledgement onAccept={handleAcceptResubmissionAcknowledgement} onCancel={onClose} />;
      $[39] = onClose;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    submissionContent = t16;
  } else {
    if (sourceDrop && replacementDrop) {
      let t16;
      if ($[41] !== deleteOriginalError || $[42] !== handleDeleteOriginalClick || $[43] !== handleKeepBoth || $[44] !== isDeletingOriginal || $[45] !== replacementDrop || $[46] !== sourceDrop) {
        t16 = <ResubmitDeleteConfirmation originalDrop={sourceDrop} replacementDrop={replacementDrop} isDeleting={isDeletingOriginal} error={deleteOriginalError} onDeleteOriginal={handleDeleteOriginalClick} onKeepBoth={handleKeepBoth} />;
        $[41] = deleteOriginalError;
        $[42] = handleDeleteOriginalClick;
        $[43] = handleKeepBoth;
        $[44] = isDeletingOriginal;
        $[45] = replacementDrop;
        $[46] = sourceDrop;
        $[47] = t16;
      } else {
        t16 = $[47];
      }
      submissionContent = t16;
    } else {
      let t16;
      if ($[48] !== form || $[49] !== handleArtworkCommentaryMediaChange || $[50] !== handleBackFromAdditionalInfo || $[51] !== handleOpenPreview || $[52] !== handlePreviewImageChange || $[53] !== handlePromoVideoChange || $[54] !== handleSubmitClick || $[55] !== isPreviewMode || $[56] !== isSubmitting || $[57] !== onClose || $[58] !== previewDrop || $[59] !== submissionError || $[60] !== submissionPhase || $[61] !== submitLabel || $[62] !== uploadProgress || $[63] !== wave) {
        t16 = <MemesArtSubmissionStepContent form={form} wave={wave} isPreviewMode={isPreviewMode} previewDrop={previewDrop} isSubmitting={isSubmitting} submissionPhase={submissionPhase} uploadProgress={uploadProgress} submissionError={submissionError} submitLabel={submitLabel} onClose={onClose} onBackToEdit={handleBackToEdit} onBackFromAdditionalInfo={handleBackFromAdditionalInfo} onOpenPreview={handleOpenPreview} onSubmitClick={handleSubmitClick} onArtworkCommentaryMediaChange={handleArtworkCommentaryMediaChange} onPreviewImageChange={handlePreviewImageChange} onPromoVideoChange={handlePromoVideoChange} />;
        $[48] = form;
        $[49] = handleArtworkCommentaryMediaChange;
        $[50] = handleBackFromAdditionalInfo;
        $[51] = handleOpenPreview;
        $[52] = handlePreviewImageChange;
        $[53] = handlePromoVideoChange;
        $[54] = handleSubmitClick;
        $[55] = isPreviewMode;
        $[56] = isSubmitting;
        $[57] = onClose;
        $[58] = previewDrop;
        $[59] = submissionError;
        $[60] = submissionPhase;
        $[61] = submitLabel;
        $[62] = uploadProgress;
        $[63] = wave;
        $[64] = t16;
      } else {
        t16 = $[64];
      }
      submissionContent = t16;
    }
  }
  const t16 = isResubmission ? "Resubmit Work to The Memes" : "Submit Work to The Memes";
  let t17;
  if ($[65] !== onClose || $[66] !== shellDescription || $[67] !== submissionContent || $[68] !== t16) {
    t17 = <MemesArtSubmissionShell title={t16} description={shellDescription} onClose={onClose}>{submissionContent}</MemesArtSubmissionShell>;
    $[65] = onClose;
    $[66] = shellDescription;
    $[67] = submissionContent;
    $[68] = t16;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  return t17;
};
export default MemesArtSubmissionContainer;
function _temp(phase) {
  console.warn(`Submission phase changed to: ${phase}`);
}